import { useSnackbar } from "notistack";
import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { extractResultsFromResponse, OptionsData } from "../types";
import { apiFetchWithRetry, REACT_APP_API_ROOT } from "../utilities/api";
import { fetchEndpointData } from "../utilities/dataSourceAdapters";
import { enqueueUniqueSnackbar } from "../utilities/snackbarUtils";
import { useConfig } from "./ConfigContext";
import { authTokenService } from "../utilities/authTokenService";

interface OptionsContextType {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  loadOptionsForEndpoint: (endpoints: string | string[]) => Promise<void>;
  forceRefreshEndpoint: (endpoint: string) => Promise<void>;
  removeOptionItem: (endpoint: string, id: string | number) => void;
  upsertOptionItem: (endpoint: string, item: { id: string | number; name: string }) => void;
  isLoading: boolean;
}

// Create the context with a default value matching the interface
export const OptionsContext = createContext<OptionsContextType>({
  options: null,
  refreshOptions: async () => {},
  loadOptionsForEndpoint: async () => {},
  forceRefreshEndpoint: async () => {},
  removeOptionItem: () => {},
  upsertOptionItem: () => {},
  isLoading: false
});

export const OptionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<OptionsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEndpoints, setLoadingEndpoints] = useState<Set<string>>(new Set());
  const config = useConfig();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = authTokenService();

  // Refs so loadOptionsForEndpoint can read the latest state without those
  // values being in its dependency array. This keeps the callback reference
  // stable across option/loading-state changes, which prevents EditorFilter's
  // useEffect from firing in a cascade every time a batch of options loads.
  const optionsRef = useRef(options);
  const loadingEndpointsRef = useRef(loadingEndpoints);
  optionsRef.current = options;
  loadingEndpointsRef.current = loadingEndpoints;

  const loadOptionsForEndpoint = useCallback(
    async (endpoints: string | string[]) => {
      if (!accessToken) {
        return;
      }

      const endpointsArray = Array.isArray(endpoints) ? endpoints : [endpoints];

      // Use refs so this callback doesn't need options/loadingEndpoints as deps.
      const endpointsToLoad = endpointsArray.filter(
        (endpoint) => !loadingEndpointsRef.current.has(endpoint) && (!optionsRef.current || !optionsRef.current[endpoint])
      );

      if (endpointsToLoad.length === 0) {
        return;
      }

      // NOTE: intentionally no startLoading() here — options loading is a
      // background operation and must never block the UI with a spinner overlay.
      setIsLoading(true);
      setLoadingEndpoints((prev) => {
        const next = new Set(prev);
        endpointsToLoad.forEach((endpoint) => next.add(endpoint));
        return next;
      });

      try {
        const targetEndpoints = config.apiEndpoints.filter(
          (endpoint) => endpointsToLoad.includes(endpoint.tableName) && endpoint.dataSourceType !== "fulcrum" // CM-FE160-32: skip Fulcrum — no API key in this context
        );

        const fetchPromises = targetEndpoints.map(async (endpoint) => {
          try {
            const result = await fetchEndpointData(endpoint, {
              fields: "id,name"
            });

            const data = result.data;

            const transformedData = data.map((item) => ({
              id: item.id,
              name: item.name,
              description: "", // Not needed for dropdowns, set empty to match interface
              thumbnail: "" // Not needed for dropdowns, set empty to match interface
            }));

            return [endpoint.tableName, transformedData];
          } catch (error) {
            console.warn(`Failed to fetch ${endpoint.name} after retries:`, error);
            enqueueUniqueSnackbar(enqueueSnackbar, `Failed to load ${endpoint.name}. Please try again later.`, { variant: "error" });
            return [endpoint.tableName, []];
          }
        });

        const results = await Promise.all(fetchPromises);
        const newOptionsData = Object.fromEntries(results);

        setOptions((prev) => ({ ...prev, ...newOptionsData }));
      } catch (error) {
        console.error("Error loading options:", error);
        enqueueUniqueSnackbar(enqueueSnackbar, "Failed to load some options. Please refresh the page.", { variant: "error" });
      } finally {
        setLoadingEndpoints((prev) => {
          const next = new Set(prev);
          endpointsToLoad.forEach((endpoint) => next.delete(endpoint));
          return next;
        });
        setIsLoading(false);
      }
    },
    // Stable deps — options and loadingEndpoints are read via refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, accessToken, enqueueSnackbar]
  );

  const forceRefreshEndpoint = useCallback(
    async (endpointTableName: string) => {
      if (!accessToken) {
        return;
      }

      try {
        const targetEndpoint = config.apiEndpoints.find((endpoint) => endpoint.tableName === endpointTableName);

        if (!targetEndpoint) {
          console.warn(`Endpoint not found: ${endpointTableName}`);
          return;
        }

        console.log(`[OptionsContext] Force refreshing ${targetEndpoint.name}`);

        const response = await apiFetchWithRetry(`${REACT_APP_API_ROOT}${targetEndpoint.api_endpoint}?fields=id,name&page_size=10000`);

        const data = extractResultsFromResponse<any>(response);
        const transformedData = data.map((item) => ({
          id: item.id,
          name: item.name,
          description: "", // Not needed for dropdowns
          thumbnail: "" // Not needed for dropdowns
        }));

        // Update just this endpoint's options
        setOptions((prev) => ({
          ...prev,
          [endpointTableName]: transformedData
        }));

        console.log(`[OptionsContext] Successfully refreshed ${targetEndpoint.name}: ${transformedData.length} items`);
      } catch (error) {
        console.error(`Error force refreshing ${endpointTableName}:`, error);
        enqueueUniqueSnackbar(enqueueSnackbar, `Failed to refresh ${endpointTableName}. Please try again later.`, { variant: "error" });
      }
    },
    [config, accessToken, enqueueSnackbar]
  );

  // Remove a single item from an endpoint's cached options without refetching
  const removeOptionItem = useCallback((endpointTableName: string, id: string | number) => {
    setOptions((prev) => {
      if (!prev || !prev[endpointTableName]) return prev;
      return {
        ...prev,
        [endpointTableName]: prev[endpointTableName].filter((item) => String(item.id) !== String(id))
      };
    });
  }, []);

  // Add or update a single item in an endpoint's cached options without refetching
  const upsertOptionItem = useCallback((endpointTableName: string, item: { id: string | number; name: string }) => {
    setOptions((prev) => {
      if (!prev || !prev[endpointTableName]) return prev;
      const existing = prev[endpointTableName];
      const updatedItem = { id: item.id, name: item.name, description: "", thumbnail: "" };
      const index = existing.findIndex((o) => String(o.id) === String(item.id));
      const next = index >= 0 ? existing.map((o, i) => (i === index ? updatedItem : o)) : [...existing, updatedItem];
      return { ...prev, [endpointTableName]: next };
    });
  }, []);

  const refreshOptions = useCallback(async () => {
    setOptions(null);
    const previousEndpoints = options ? Object.keys(options) : [];
    if (previousEndpoints.length > 0) {
      try {
        const targetEndpoints = config.apiEndpoints.filter(
          (endpoint) => previousEndpoints.includes(endpoint.tableName) && endpoint.dataSourceType !== "fulcrum" // CM-FE160-32: skip Fulcrum on refresh — no API key
        );

        // TODO: this page size is limited to 5000 items for options. Its still a bit slow for large amounts of data, like fetching 2000 Places. We need to warn user if there is more data.
        // We Should also allow fetching more options from server somehow.
        const fetchPromises = targetEndpoints.map(async (endpoint) => {
          try {
            let data: any[] = [];

            // Use generic adapter function for all data sources
            const result = await fetchEndpointData(endpoint, {
              fields: "id,name"
            });

            data = result.data;
            console.log(`[OptionsContext] Refreshed ${data.length} records for ${endpoint.name} using generic adapter`);

            return [
              endpoint.tableName,
              data.map((item) => ({
                id: item.id,
                name: item.name,
                description: "", // Not needed for dropdowns, set empty to match interface
                thumbnail: "" // Not needed for dropdowns, set empty to match interface
              }))
            ];
          } catch (error) {
            console.warn(`Failed to fetch ${endpoint.name} after retries:`, error);
            enqueueUniqueSnackbar(enqueueSnackbar, `Failed to refresh ${endpoint.name}. Please try again later.`, { variant: "error" });
            return [endpoint.tableName, []];
          }
        });

        const results = await Promise.all(fetchPromises);
        const newOptionsData = Object.fromEntries(results);
        setOptions((prev) => ({ ...prev, ...newOptionsData }));
      } catch (error) {
        console.error("Error refreshing options:", error);
        enqueueUniqueSnackbar(enqueueSnackbar, "Failed to refresh some options. Please try again later.", { variant: "error" });
      }
    }
  }, [config, options, enqueueSnackbar]);

  return (
    <OptionsContext.Provider
      value={{ options, refreshOptions, loadOptionsForEndpoint, forceRefreshEndpoint, removeOptionItem, upsertOptionItem, isLoading }}
    >
      {children}
    </OptionsContext.Provider>
  );
};

export const useOptions = () => {
  const context = useContext(OptionsContext);
  if (context === undefined) {
    throw new Error("useOptions must be used within an OptionsProvider");
  }
  return context;
};
